import { createStyles, FormHelperText, WithStyles, withStyles } from '@material-ui/core'
import Slider from '@material-ui/core/Slider'
import classNames from 'classnames'
import { isNumber } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RangedInput from './SimpleSlider'

const styles = () =>
  createStyles({
    root: {
      borderTopLeftRadius: 'inherit',
      borderTopRightRadius: 'inherit',
      width: '100%',
      fontSize: '12px',
    },
    left: {
      fontSize: '10px',
      float: 'left',
      left: '5%',
    },
    right: {
      fontSize: '10px',
      float: 'right',
      right: '5%',
    },
    unit: {
      paddingLeft: '5px',
      display: 'inline-block',
      fontSize: '10px',
      color: 'grey',
      verticalAlign: 'bottom',
    },
  })

interface IProps extends WithStyles<typeof styles> {
  TextInput?: any
  className?: string
  clearable?: boolean
  disableFuture?: boolean
  disabled?: boolean
  error?: { max: boolean; min: boolean }
  formatValue?: (value: number, index?: number) => string
  helperText?: React.ReactNode
  locale?: string
  // Max value excluded
  max: number
  // Min value excluded
  min: number
  onChange: (range: number | [number, number]) => void
  step?: number
  unit?: string
  value: number | number[]
}

const SliderInput = (props: IProps) => {
  const {
    value,
    onChange,
    max,
    min,
    disableFuture,
    clearable,
    error = { min: false, max: false },
    classes,
    className,
    helperText,
    unit,
    disabled,
    locale,
    formatValue,
    TextInput,
    step,
  } = props
  const { t } = useTranslation()
  const [buffer, setBuffer] = useState<number | number[]>(value)

  useEffect(() => setBuffer(value), [value])

  let values

  if (!isNumber(buffer) && buffer[0] !== undefined && buffer[1] !== undefined) {
    values = (
      <div>
        <span className={classNames(classes.left, className)}>{formatValue ? formatValue(buffer[0]) : buffer[0]}</span>
        <span className={classNames(classes.right, className)}>{formatValue ? formatValue(buffer[1]) : buffer[1]}</span>
      </div>
    )
  }

  return (
    <div className={classNames(classes.root, className)}>
      {values}
      <Slider
        max={typeof max === 'number' ? max : parseInt(max, 10)}
        min={typeof min === 'number' ? min : parseInt(min, 10)}
        getAriaValueText={formatValue}
        disabled={disabled}
        value={buffer}
        step={step}
        onChange={(_event: React.ChangeEvent<Record<string, unknown>>, d: number | number[]) => {
          setBuffer(d as number | [number, number])
        }}
        onChangeCommitted={(_event: React.ChangeEvent<Record<string, unknown>>, d: number | number[]) => {
          onChange(d as number | [number, number])
        }}
        // FIXME (tlaurent 2023/10/25): The ::after of this Slider increase the width of the component, overflowing in x
        // It's seems to be correctible with a position: relative
      />
      {TextInput}
      {!isNumber(value) && !TextInput && (
        <>
          <RangedInput
            disabled={disabled}
            float={!Number.isInteger(step)}
            label={t('From')}
            min={min}
            max={value[1]}
            value={value[0]}
            onChange={(d: number | undefined) => {
              if (d && d < value[1] && d > min) {
                onChange([d, value[1]])
              } else {
                onChange([min, value[1]])
              }
            }}
            disableFuture={disableFuture}
            clearable={clearable}
            error={error.min}
            locale={locale}
          />
          <p className={classNames(classes.unit, className)}>{unit}</p>
          <div style={{ width: '10%', display: 'inline-block' }} />
          <RangedInput
            disabled={disabled}
            float={!Number.isInteger(step)}
            label={t('To')}
            min={isNumber(value) ? min : value[0]}
            max={max + 1}
            value={isNumber(value) ? value : value[1]}
            onChange={(d: number | undefined) => {
              if (d && d > value[0] && d < max) {
                onChange([value[0], d])
              } else {
                onChange([value[0], max])
              }
            }}
            disableFuture={disableFuture}
            clearable={clearable}
            error={error.max}
            locale={locale}
          />
        </>
      )}
      <p className={classNames(classes.unit, className)}>{unit}</p>
      <br />
      <div style={{ display: error.min || error.max ? 'block' : 'none' }}>
        <FormHelperText component="div" error={error.min || error.max}>
          {helperText}
        </FormHelperText>
      </div>
    </div>
  )
}

export default withStyles(styles)(SliderInput)
